import { TYPES } from '../../render/constants';

// Shared lock icon size constant
const LOCK_ICON_SIZE = 22;

/**
 * Draw a lock icon at the specified position
 * @param {CanvasRenderingContext2D} context - Canvas 2D context
 * @param {number} iconX - X position of the icon (top-left)
 * @param {number} iconY - Y position of the icon (top-left)
 * @param {number} iconSize - Size of the icon
 */
function drawLockIcon(context, iconX, iconY, iconSize = LOCK_ICON_SIZE) {
  const scale = iconSize / 24;

  context.strokeStyle = '#ffffff';
  context.fillStyle = '#ffffff';
  context.lineWidth = 1.5 * scale;
  context.lineCap = 'round';
  context.lineJoin = 'round';

  // Lock body - moved down to make room for taller shackle
  const bodyX = iconX + 3 * scale;
  const bodyY = iconY + 11 * scale;
  const bodyW = 18 * scale;
  const bodyH = 11 * scale;
  const bodyRadius = 2 * scale;

  context.beginPath();
  context.moveTo(bodyX + bodyRadius, bodyY);
  context.arcTo(bodyX + bodyW, bodyY, bodyX + bodyW, bodyY + bodyH, bodyRadius);
  context.arcTo(bodyX + bodyW, bodyY + bodyH, bodyX, bodyY + bodyH, bodyRadius);
  context.arcTo(bodyX, bodyY + bodyH, bodyX, bodyY, bodyRadius);
  context.arcTo(bodyX, bodyY, bodyX + bodyW, bodyY, bodyRadius);
  context.closePath();
  context.stroke();

  // Lock shackle - taller arc for better visual balance
  const shackleX = iconX + 7 * scale;
  const shackleY = iconY + 3 * scale;
  const shackleW = 10 * scale;
  const shackleH = 8 * scale;
  const shackleRadius = 5 * scale;

  context.beginPath();
  context.moveTo(shackleX, shackleY + shackleH);
  context.lineTo(shackleX, shackleY + shackleRadius);
  context.arc(shackleX + shackleW / 2, shackleY + shackleRadius, shackleRadius, Math.PI, 0, false);
  context.lineTo(shackleX + shackleW, shackleY + shackleH);
  context.stroke();
}

/**
 * Render a lock indicator icon near the node when it's locked
 * Position is consistent with renderSizeLabel (below the node, centered)
 * @param {CanvasRenderingContext2D} context - Canvas 2D context
 * @param {Object} region - Transformed region of the node
 */
export function renderLockIndicator(context, region) {
  const paddingX = 8;
  const paddingY = 4;

  // Position at bottom center, same as size label
  const labelX = region.center.x;
  const labelY = region.bottomLeft.y + 48;

  // Background pill dimensions - same style as size label
  const bgWidth = LOCK_ICON_SIZE + paddingX * 2;
  const bgHeight = 28 + paddingY * 2;
  const cornerRadius = 12;

  context.save();

  // Draw background with subtle shadow (same style as size label)
  context.shadowColor = 'rgba(0, 0, 0, 0.1)';
  context.shadowBlur = 4;
  context.shadowOffsetY = 2;

  context.fillStyle = '#8b3eff'; // Same color as size label
  context.beginPath();
  context.moveTo(labelX - bgWidth / 2 + cornerRadius, labelY - bgHeight / 2);
  context.arcTo(
    labelX + bgWidth / 2,
    labelY - bgHeight / 2,
    labelX + bgWidth / 2,
    labelY + bgHeight / 2,
    cornerRadius
  );
  context.arcTo(
    labelX + bgWidth / 2,
    labelY + bgHeight / 2,
    labelX - bgWidth / 2,
    labelY + bgHeight / 2,
    cornerRadius
  );
  context.arcTo(
    labelX - bgWidth / 2,
    labelY + bgHeight / 2,
    labelX - bgWidth / 2,
    labelY - bgHeight / 2,
    cornerRadius
  );
  context.arcTo(
    labelX - bgWidth / 2,
    labelY - bgHeight / 2,
    labelX + bgWidth / 2,
    labelY - bgHeight / 2,
    cornerRadius
  );
  context.closePath();
  context.fill();

  // Reset shadow
  context.shadowColor = 'transparent';
  context.shadowBlur = 0;
  context.shadowOffsetY = 0;

  // Draw lock icon centered in the pill
  const iconX = labelX - LOCK_ICON_SIZE / 2;
  const iconY = labelY - LOCK_ICON_SIZE / 2;
  drawLockIcon(context, iconX, iconY, LOCK_ICON_SIZE);

  context.restore();
}

function renderSizeLabel(context, node, region, fillColor, isLocked = false) {
  // Improved text styling
  context.font = 'bold 20px Inter, Arial, sans-serif';
  context.textAlign = 'center';
  context.textBaseline = 'middle';

  const labelText = `${Math.round(node.w)}x${Math.round(node.h)}`;
  const labelX = region.center.x;
  const labelY = region.bottomLeft.y + 48; // Slightly increased distance from bottom

  // Calculate lock icon dimensions if locked
  const lockIconSpacing = isLocked ? 4 : 0;
  const lockIconWidth = isLocked ? LOCK_ICON_SIZE + lockIconSpacing : 0;

  // Improved background styling
  const textMetrics = context.measureText(labelText);
  const paddingX = 8; // Increased padding
  const paddingY = 4; // Increased padding
  const bgWidth = textMetrics.width + paddingX * 2 + lockIconWidth;
  const bgHeight = 28 + paddingY * 2;
  const cornerRadius = 12; // Larger rounded corners

  // Background with subtle shadow
  context.shadowColor = 'rgba(0, 0, 0, 0.1)';
  context.shadowBlur = 4;
  context.shadowOffsetY = 2;

  context.fillStyle = fillColor ?? '#8b3eff'; // More opaque background
  context.beginPath();
  context.moveTo(labelX - bgWidth / 2 + cornerRadius, labelY - bgHeight / 2);
  context.arcTo(
    labelX + bgWidth / 2,
    labelY - bgHeight / 2,
    labelX + bgWidth / 2,
    labelY + bgHeight / 2,
    cornerRadius
  );
  context.arcTo(
    labelX + bgWidth / 2,
    labelY + bgHeight / 2,
    labelX - bgWidth / 2,
    labelY + bgHeight / 2,
    cornerRadius
  );
  context.arcTo(
    labelX - bgWidth / 2,
    labelY + bgHeight / 2,
    labelX - bgWidth / 2,
    labelY - bgHeight / 2,
    cornerRadius
  );
  context.arcTo(
    labelX - bgWidth / 2,
    labelY - bgHeight / 2,
    labelX + bgWidth / 2,
    labelY - bgHeight / 2,
    cornerRadius
  );
  context.closePath();
  context.fill();

  // Reset shadow for text
  context.shadowColor = 'transparent';
  context.shadowBlur = 0;
  context.shadowOffsetY = 0;

  // Draw lock icon if locked
  if (isLocked) {
    const iconX = labelX - bgWidth / 2 + paddingX;
    const iconY = labelY - LOCK_ICON_SIZE / 2;
    drawLockIcon(context, iconX, iconY, LOCK_ICON_SIZE);
  }

  // Text - offset if lock icon is present
  const textX = isLocked ? labelX + lockIconWidth / 2 : labelX;
  context.fillStyle = '#fff';
  context.fillText(labelText, textX, labelY + 2);
}

export function drawRegion(context, stageContext, region) {
  const { scale } = stageContext.providers;

  const pointer = region.transform(scale.scale, scale.translateX, scale.translateY);

  context.beginPath();
  context.rect(pointer.x, pointer.y, pointer.w, pointer.h);
  context.fillStyle = 'rgba(255, 0, 0 ,0.5)';
  context.fill();
  context.strokeStyle = 'black';
  context.lineWidth = 1;
  context.stroke();
}

export function renderInteractiveNodeBorder(
  renderContext,
  context,
  node,
  labelSize,
  { lineWidth } = {}
) {
  const config = renderContext.configs?.interactive || {};
  const selectionColor = config.selectionColor ?? '#8b3eff';
  const selectionLineWidth = config.selectionLineWidth ?? 4;

  const { scale } = renderContext.providers;
  const region = renderContext.api
    .getNodeRegion(node)
    .transform(scale.scale, scale.translateX, scale.translateY);

  // Check if node is locked
  const isLocked = renderContext.api.isNodeLocked(node);

  context.strokeStyle = selectionColor;
  context.lineWidth = lineWidth !== undefined ? lineWidth : selectionLineWidth;

  context.beginPath();
  context.moveTo(region.topLeft.x, region.topLeft.y);
  context.lineTo(region.topRight.x, region.topRight.y);
  context.lineTo(region.bottomRight.x, region.bottomRight.y);
  context.lineTo(region.bottomLeft.x, region.bottomLeft.y);
  context.lineTo(region.topLeft.x, region.topLeft.y);
  context.closePath();
  context.stroke();
  context.strokeStyle = '';

  if (node.type === TYPES.frame && labelSize) {
    renderSizeLabel(context, node, region, selectionColor, isLocked);
  } else if (isLocked) {
    // Render lock indicator if node is locked (for non-frame nodes)
    renderLockIndicator(context, region);
  }
}

export function drawRectRegion(
  context,
  rect,
  { fill = 'rgba(0, 0, 255, 0.2)', lineWidth = 4, stroke = '#8b3eff' } = {}
) {
  const startX = rect.x;
  const startY = rect.y;

  const width = rect.w;
  const height = rect.h;

  context.strokeStyle = stroke;
  context.lineWidth = lineWidth;
  context.fillStyle = fill;

  context.beginPath();
  context.rect(startX, startY, width, height);
  context.fill();
  context.stroke();
}
